import { ApolloError } from '@apollo/client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { SIMULATION } from '~queries';
import { selectCartProducts } from '~redux/reducers/cartSlice';
import type {
  GetSimulationQuery,
  GetSimulationQueryVariables,
  Simulation,
} from '~types/appsync.types';
import type { CartProduct, ProductDisplay } from '~types/cart';
import { buildFlatParticipants, buildProductDisplays } from '~utils';

import useApiGatewayQuery from '../useApiGatewayQuery/useApiGatewayQuery';

interface UseCartResult {
  products: CartProduct[];
  productDisplays: ProductDisplay[];
  simulation: Simulation | undefined;
  isPriceLoading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
}

export const useCart = (): UseCartResult => {
  const products = useSelector(selectCartProducts);

  const flatParticipants = useMemo(() => buildFlatParticipants(products), [products]);
  const participants = useMemo(
    () => flatParticipants.map((flatParticipant) => flatParticipant.input),
    [flatParticipants],
  );

  const firstProduct = products[0];
  const skip = !firstProduct;

  const { data, loading, error, refetch } = useApiGatewayQuery<
    GetSimulationQuery,
    GetSimulationQueryVariables
  >(SIMULATION, {
    skip,
    variables: skip
      ? undefined
      : {
          catalogSelectable: firstProduct.catalogSelectable,
          contractorId: firstProduct.contractorId,
          date: firstProduct.date,
          participants,
          productCategoryShortname: firstProduct.productCategoryShortname,
          productCategoryVisible: firstProduct.productCategoryVisible,
          validityCategoryShortname: firstProduct.validityCategoryShortname,
          validityCategoryVisible: firstProduct.validityCategoryVisible,
        },
  });

  const simulation = data?.simulation ?? undefined;
  const productDisplays = useMemo(
    () => buildProductDisplays(products, flatParticipants, simulation),
    [products, flatParticipants, simulation],
  );

  const isPriceLoading = loading && !data && products.length > 0;

  return {
    error,
    isPriceLoading,
    productDisplays,
    products,
    refetch: () => {
      refetch().catch((err) => console.warn('[cart] refetch failed', err));
    },
    simulation,
  };
};
